import Utils from '~/modules/Utils'

/**
 * MVP hardcoded rollout of the anti-copy blur protection (see gallery-prd.md / Slack thread
 * with Rúbia, Sept/2026): the enlarged photo shows partially blurred, revealing the rest
 * through one of a few interactions, so a print never lands on the whole sharp photo.
 *
 * Two interaction styles, depending on `mode` (see `usesDragInteraction`):
 * - diagonal/vertical/horizontal: a tap flips which half is revealed. Dragging does
 *   nothing, so a print (which needs no interaction beyond a tap) never catches a region
 *   the finger happened to be dragging over.
 * - circular/band ("faixa"): dragging the finger moves the revealed area (a lens or a
 *   strip) across the photo, like a magnifying glass. Since this needs a drag gesture on
 *   the photo, swipe-to-navigate is disabled while either mode is active.
 *
 * `resolve(customerId, apiMobileAntiCopy)`: `apiMobileAntiCopy` (from gallery-cp/gallery-api,
 * `frontData.mobileAntiCopy`) wins whenever enabled; otherwise falls back to the hardcoded
 * allowlist below.
 */

const ENABLED_CUSTOMER_IDS = []

// MVP: only mobile browsers. Desktop keeps today's behaviour (print-triggered blur via
// ps-go/anti-copy.js, which already works on Chrome desktop).
const MOBILE_ONLY = true

const MODES = ['diagonal', 'vertical', 'horizontal', 'circular', 'band']

// circular/band are dragged to reveal -- the others are tap-to-toggle. Used to decide
// whether swipe-to-navigate has to be disabled (PicturePreview/index.js, both trees).
const DRAG_MODES = ['circular', 'band']

const DEFAULT_SETTINGS = {
  // MVP: only diagonal is enabled for real; vertical/horizontal/circular/band are
  // implemented and reachable via ?blurMode=... for testing/demoing, but not yet offered.
  mode: 'circular',
  intensity: 35
}

// Per-customer mode override, applied on top of DEFAULT_SETTINGS.mode. MVP: customer 4092
// gets the circular mode instead of the default diagonal; every other enabled customer
// keeps the default.
const CUSTOMER_MODE_OVERRIDES = {
  39772: 'diagonal'
}

const BAND_WIDTH_PERCENT = 26
const CIRCLE_RADIUS_PERCENT = 19
const MIN_BLUR_PX = 4
const MAX_BLUR_PX = 40
// A touch of saturation counteracts blur's tendency to make the photo look flat/washed-out.
const BLUR_SATURATION = 1.05

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

function usesDragInteraction(mode) {
  return DRAG_MODES.indexOf(mode) !== -1
}

// Test-only override so the mode/intensity can be demoed without a redeploy, e.g.
// ?blurMode=circular&blurIntensity=60. It can never disable the protection or bypass the
// customer allowlist.
function getTestOverrides() {
  if (typeof window === 'undefined' || !window.location) return {}

  const params = new URLSearchParams(window.location.search)
  const overrides = {}

  const mode = params.get('blurMode')
  if (mode && MODES.indexOf(mode) !== -1) overrides.mode = mode

  const intensity = params.get('blurIntensity')
  if (intensity !== null && !Number.isNaN(Number(intensity))) {
    overrides.intensity = clamp(Number(intensity), 0, 100)
  }

  return overrides
}

function resolve(customerId, apiMobileAntiCopy) {
  if (apiMobileAntiCopy && apiMobileAntiCopy.enabled) {
    if (MOBILE_ONLY && !Utils.isMobileOrTablet()) return null

    return {
      mode: MODES.indexOf(apiMobileAntiCopy.mode) !== -1 ? apiMobileAntiCopy.mode : DEFAULT_SETTINGS.mode,
      intensity: DEFAULT_SETTINGS.intensity,
      ...getTestOverrides()
    }
  }

  if (ENABLED_CUSTOMER_IDS.indexOf(customerId) === -1) return null
  if (MOBILE_ONLY && !Utils.isMobileOrTablet()) return null

  return {
    ...DEFAULT_SETTINGS,
    mode: CUSTOMER_MODE_OVERRIDES[customerId] || DEFAULT_SETTINGS.mode,
    ...getTestOverrides()
  }
}

function buildBlurFilter(intensity) {
  const ratio = clamp(intensity, 0, 100) / 100
  const radius = Math.round(MIN_BLUR_PX + (ratio * (MAX_BLUR_PX - MIN_BLUR_PX)))
  return `blur(${radius}px) saturate(${BLUR_SATURATION})`
}

// Pixel radius of the circular reveal, given the container's own pixel size -- the same
// formula the CSS engine uses to resolve `circle(<percentage> at ...)` internally (per
// spec, a percentage radius resolves against sqrt((w²+h²)/2)). Lets the UI draw a ring
// that exactly matches the clip-path circle it's decorating, in plain pixels.
function circleRadiusPx(containerWidth, containerHeight) {
  if (!containerWidth || !containerHeight) return 0
  const diagonal = Math.sqrt(((containerWidth * containerWidth) + (containerHeight * containerHeight)) / 2)
  return (CIRCLE_RADIUS_PERCENT / 100) * diagonal
}

/**
 * Returns a CSS `clip-path` value (no vendor prefix needed on the target browsers, iOS
 * Safari 13+ and Chrome Android -- unlike the `mask-image` this used to be built with).
 * `revealState` carries both interaction styles so callers don't need to branch on mode
 * themselves:
 * - `isFlipped` (boolean): which half is revealed, for the tap-to-flip modes.
 * - `position` ({x, y}, 0-1 each): the lens/strip center, for circular/band (band ignores
 *   `y`). Ignored by every other mode.
 */
function buildClipPath(mode, revealState) {
  const { isFlipped, position } = revealState || {}
  const x = clamp((position && position.x != null) ? position.x : 0.5, 0, 1) * 100
  const y = clamp((position && position.y != null) ? position.y : 0.5, 0, 1) * 100

  switch (mode) {
    case 'vertical':
      return isFlipped ? 'inset(0 0 0 50%)' : 'inset(0 50% 0 0)'
    case 'horizontal':
      return isFlipped ? 'inset(50% 0 0 0)' : 'inset(0 0 50% 0)'
    case 'circular':
      // Percentage radius resolves against sqrt((w²+h²)/2) per spec -- a true circle
      // regardless of the photo's aspect ratio, unlike a percentage mask-image radius.
      return `circle(${CIRCLE_RADIUS_PERCENT}% at ${x}% ${y}%)`
    case 'band': {
      const half = BAND_WIDTH_PERCENT / 2
      const left = clamp(x - half, 0, 100)
      const right = clamp(100 - (x + half), 0, 100)
      return `inset(0 ${right}% 0 ${left}%)`
    }
    case 'diagonal':
    default:
      // True corner-to-corner triangles regardless of aspect ratio -- a fixed-angle
      // gradient (the previous approach) only cuts corner-to-corner on a square box.
      return isFlipped
        ? 'polygon(0 0, 100% 0, 0 100%)'
        : 'polygon(100% 0, 100% 100%, 0 100%)'
  }
}

export default {
  MODES,
  resolve,
  usesDragInteraction,
  buildBlurFilter,
  buildClipPath,
  circleRadiusPx
}
